import React, { useState, useEffect, useCallback } from 'react';
import { useStoreContext } from '../contexts/StoreContext.jsx';

export function AdvancedStoreSearch({ onResults, className = '' }) {
  const { stores, config } = useStoreContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    features: [],
    services: [],
    city: '',
    openNow: false,
    distance: null,
    userLocation: null
  });
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query, filterOptions) => {
      performSearch(query, filterOptions);
    }, 300),
    [stores]
  );

  useEffect(() => {
    if (searchQuery || Object.values(filters).some(f => f && (Array.isArray(f) ? f.length > 0 : true))) {
      setIsSearching(true);
      debouncedSearch(searchQuery, filters);
    } else {
      onResults && onResults(stores);
      setIsSearching(false);
    }
  }, [searchQuery, filters, stores, debouncedSearch, onResults]);

  const performSearch = async (query, filterOptions) => {
    try {
      let results = [...stores];

      // Text search
      if (query.trim()) {
        const lowerQuery = query.toLowerCase();
        results = results.filter(store => 
          store.name.toLowerCase().includes(lowerQuery) ||
          store.city.toLowerCase().includes(lowerQuery) ||
          store.address.toLowerCase().includes(lowerQuery) ||
          (store.specialties && store.specialties.some(s => 
            s.toLowerCase().includes(lowerQuery)
          )) ||
          (store.features && store.features.some(f => 
            f.toLowerCase().includes(lowerQuery)
          ))
        );
      }

      // Feature filters
      if (filterOptions.features.length > 0) {
        results = results.filter(store =>
          store.features && filterOptions.features.every(feature =>
            store.features.includes(feature)
          )
        );
      }

      // Service filters
      if (filterOptions.services.length > 0) {
        results = results.filter(store =>
          store.services && filterOptions.services.every(service =>
            store.services.includes(service)
          )
        );
      }

      // City filter
      if (filterOptions.city) {
        results = results.filter(store =>
          store.city.toLowerCase().includes(filterOptions.city.toLowerCase())
        );
      }

      // Open now filter
      if (filterOptions.openNow) {
        results = results.filter(store => isStoreOpenNow(store));
      }

      // Distance filter
      if (filterOptions.distance && filterOptions.userLocation) {
        results = results.filter(store => {
          const distance = calculateDistance(
            filterOptions.userLocation.lat,
            filterOptions.userLocation.lng,
            store.lat,
            store.lng
          );
          return distance <= filterOptions.distance;
        });
      }

      // Sort by relevance and distance
      results = sortSearchResults(results, query, filterOptions.userLocation);

      onResults && onResults(results);
      setIsSearching(false);
    } catch (error) {
      console.error('Search error:', error);
      setIsSearching(false);
    }
  };

  const handleGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setFilters(prev => ({ ...prev, userLocation: location }));
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please check your browser settings.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilters({
      features: [],
      services: [],
      city: '',
      openNow: false,
      distance: null,
      userLocation: null
    });
  };

  const toggleFilter = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(item => item !== value)
        : [...prev[type], value]
    }));
  };

  // Get unique cities for filter dropdown
  const availableCities = [...new Set(stores.map(store => store.city))].sort();

  // Get available features and services
  const availableFeatures = [...new Set(stores.flatMap(store => store.features || []))].sort();
  const availableServices = [...new Set(stores.flatMap(store => store.services || []))].sort();

  return (
    <div className={`bg-surface border border-border rounded-xl p-6 shadow-sm ${className}`}>
      {/* Search Input */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder={config.storeLocator?.searchPlaceholder || "Search stores..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-10 pr-12 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
          />
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted">
            🔍
          </span>
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        {/* Features Filter */}
        {availableFeatures.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Features</label>
            <div className="flex flex-wrap gap-2">
              {availableFeatures.map(feature => {
                const featureConfig = config.features?.[feature] || {};
                const isSelected = filters.features.includes(feature);
                return (
                  <button
                    key={feature}
                    onClick={() => toggleFilter('features', feature)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      isSelected
                        ? featureConfig.color || 'bg-accent text-white'
                        : 'bg-background border border-border text-text-secondary hover:bg-accent/10'
                    }`}
                  >
                    {featureConfig.icon && `${featureConfig.icon} `}{feature}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Services Filter */}
        {availableServices.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Services</label>
            <div className="flex flex-wrap gap-2">
              {availableServices.map(service => {
                const isSelected = filters.services.includes(service);
                return (
                  <button
                    key={service}
                    onClick={() => toggleFilter('services', service)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      isSelected
                        ? 'bg-accent text-white'
                        : 'bg-background border border-border text-text-secondary hover:bg-accent/10'
                    }`}
                  >
                    {service}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* City Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-2">City</label>
            <select
              value={filters.city}
              onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent"
            >
              <option value="">All cities</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">Distance</label>
            <div className="flex gap-2">
              <select
                value={filters.distance || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  distance: e.target.value ? parseInt(e.target.value) : null 
                }))}
                className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent"
                disabled={!filters.userLocation}
              >
                <option value="">Any distance</option>
                <option value="1">Within 1 km</option>
                <option value="5">Within 5 km</option>
                <option value="10">Within 10 km</option>
                <option value="25">Within 25 km</option>
              </select>
              <button
                onClick={handleGeolocation}
                className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
                title="Get my location"
              >
                📍
              </button>
            </div>
          </div>
        </div>

        {/* Additional Filters */}
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.openNow}
              onChange={(e) => setFilters(prev => ({ ...prev, openNow: e.target.checked }))}
              className="rounded border-border text-accent focus:ring-accent"
            />
            <span className="text-sm text-text-secondary">Open now</span>
          </label>
        </div>

        {/* Clear Filters */}
        <div className="flex justify-between items-center pt-2 border-t border-border">
          <span className="text-sm text-text-muted">
            {stores.length} store{stores.length !== 1 ? 's' : ''} found
          </span>
          <button
            onClick={clearFilters}
            className="text-sm text-accent hover:underline"
          >
            Clear all filters
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function isStoreOpenNow(store) {
  const now = new Date();
  const dayName = now.toLocaleDateString('en', { weekday: 'long' }).toLowerCase();
  
  if (!store.hours || typeof store.hours !== 'object') return false;
  
  const todayHours = store.hours[dayName];
  if (!todayHours || todayHours.toLowerCase() === 'fermé' || todayHours.toLowerCase() === 'closed') {
    return false;
  }
  
  // Parse hours like "7:00-20:00"
  const hoursMatch = todayHours.match(/(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})/);
  if (!hoursMatch) return false;
  
  const [, openHour, openMin, closeHour, closeMin] = hoursMatch.map(Number);
  const currentHour = now.getHours();
  const currentMin = now.getMinutes();
  
  const currentTime = currentHour * 60 + currentMin;
  const openTime = openHour * 60 + openMin;
  const closeTime = closeHour * 60 + closeMin;
  
  return currentTime >= openTime && currentTime <= closeTime;
}

function sortSearchResults(results, query, userLocation) {
  return results.sort((a, b) => {
    // First priority: exact name matches
    if (query) {
      const aNameMatch = a.name.toLowerCase().includes(query.toLowerCase());
      const bNameMatch = b.name.toLowerCase().includes(query.toLowerCase());
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
    }
    
    // Second priority: distance from user
    if (userLocation) {
      const aDistance = calculateDistance(userLocation.lat, userLocation.lng, a.lat, a.lng);
      const bDistance = calculateDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
      return aDistance - bDistance;
    }
    
    // Default: alphabetical by name
    return a.name.localeCompare(b.name);
  });
}

export default AdvancedStoreSearch;
